import type { ChallengeDetails } from '@assembly-web/services/lib/types/challenges';
import { LoadingSpinner } from '@assembly-web/ui';
import { lazy, type ReactNode, Suspense } from 'react';
import { createContext, useState } from 'react';

const LaunchChallengeModal = lazy(() =>
  import('../components/modals/LaunchChallengeModal').then((module) => ({
    default: module.LaunchChallengeModal,
  }))
);
const EndChallengeModal = lazy(() =>
  import('../components/modals/EndChallengeModal').then((module) => ({
    default: module.EndChallengeModal,
  }))
);
const ArchiveChallengeModal = lazy(() =>
  import('../components/modals/ArchiveChallengeModal').then((module) => ({
    default: module.ArchiveChallengeModal,
  }))
);
const UnarchiveChallengeModal = lazy(() =>
  import('../components/modals/UnarchiveChallengeModal').then((module) => ({
    default: module.UnarchiveChallengeModal,
  }))
);

const Noop = () => null;

type ChallengeModalsContextType = {
  challengeModals: JSX.Element;
  openLaunchChallengeModal: (challenge: ChallengeDetails) => void;
  openEndChallengeModal: (challenge: ChallengeDetails) => void;
  openArchiveChallengeModal: (challenge: ChallengeDetails) => void;
  openUnarchiveChallengeModal: (challenge: ChallengeDetails) => void;
};

export const ChallengeModalsContext = createContext<ChallengeModalsContextType>(
  {
    challengeModals: <Noop />,
    openLaunchChallengeModal: () => {},
    openEndChallengeModal: () => {},
    openArchiveChallengeModal: () => {},
    openUnarchiveChallengeModal: () => {},
  }
);

export const ChallengeModalsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [selectedChallenge, setSelectedChallenge] =
    useState<ChallengeDetails | null>(null);
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [isEndModalOpen, setIsEndModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false);

  const openLaunchChallengeModal = (challenge: ChallengeDetails) => {
    setSelectedChallenge(challenge);
    setIsLaunchModalOpen(true);
  };

  const openEndChallengeModal = (challenge: ChallengeDetails) => {
    setSelectedChallenge(challenge);
    setIsEndModalOpen(true);
  };

  const openArchiveChallengeModal = (challenge: ChallengeDetails) => {
    setSelectedChallenge(challenge);
    setIsArchiveModalOpen(true);
  };

  const openUnarchiveChallengeModal = (challenge: ChallengeDetails) => {
    setSelectedChallenge(challenge);
    setIsUnarchiveModalOpen(true);
  };

  const closeModals = () => {
    setIsLaunchModalOpen(false);
    setIsEndModalOpen(false);
    setIsArchiveModalOpen(false);
    setIsUnarchiveModalOpen(false);
    setTimeout(() => setSelectedChallenge(null), 300); // delay for modal animation
  };

  // const challengeModals = (
  //   <>
  //     <LaunchChallengeModal
  //       name={selectedChallenge?.title ?? ''}
  //       open={isLaunchModalOpen}
  //       onClose={closeModals}
  //       challengeId={selectedChallenge?.id ?? ''}
  //     />
  //     <EndChallengeModal
  //       name={selectedChallenge?.title ?? ''}
  //       open={isEndModalOpen}
  //       onClose={closeModals}
  //       challengeId={selectedChallenge?.id ?? ''}
  //     />
  //   </>
  // );

  const challengeModals = (
    <Suspense
      fallback={
        <div>
          <LoadingSpinner />
        </div>
      }
    >
      {Boolean(isLaunchModalOpen) && (
        <LaunchChallengeModal
          name={selectedChallenge?.title ?? ''}
          open={isLaunchModalOpen}
          onClose={closeModals}
          challengeId={selectedChallenge?.id ?? ''}
        />
      )}
      {Boolean(isEndModalOpen) && (
        <EndChallengeModal
          name={selectedChallenge?.title ?? ''}
          open={isEndModalOpen}
          onClose={closeModals}
          challengeId={selectedChallenge?.id ?? ''}
        />
      )}
      {Boolean(isArchiveModalOpen) && (
        <ArchiveChallengeModal
          name={selectedChallenge?.title ?? ''}
          open={isArchiveModalOpen}
          onClose={closeModals}
          challengeId={selectedChallenge?.id ?? ''}
        />
      )}
      {Boolean(isUnarchiveModalOpen) && (
        <UnarchiveChallengeModal
          name={selectedChallenge?.title ?? ''}
          open={isUnarchiveModalOpen}
          onClose={closeModals}
          challengeId={selectedChallenge?.id ?? ''}
        />
      )}
    </Suspense>
  );

  return (
    <ChallengeModalsContext.Provider
      value={{
        challengeModals,
        openLaunchChallengeModal,
        openEndChallengeModal,
        openArchiveChallengeModal,
        openUnarchiveChallengeModal,
      }}
    >
      {children}
    </ChallengeModalsContext.Provider>
  );
};
